import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { TParamsChartItem } from '../../shared/utils/myTrainings'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { t } from 'i18next'
import { get, isEmpty, isNumber, toNumber } from 'lodash'
import Highcharts, { Chart } from 'highcharts/highstock'
import { sortDate } from '../../shared/utils/DateUtils'
import dayjs from 'dayjs'
import { Ttraining } from '../../shared/types/training.type'
import { useAuth } from '../../hooks/useAuth'
import { routes } from '../../pages/routes'

import UTC from 'dayjs/plugin/utc'
import { Icon } from '@iconify/react'
import { convertStrideLength, getSpeedForChart } from '../../shared/utils/conversion'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

dayjs.extend(UTC)
/**
 * Override the reset function, we don't need to hide the tooltips and crosshairs.
 */
Highcharts.Pointer.prototype.reset = function () {
  return undefined;
};

/* @ts-ignore */
Highcharts.Point.prototype.highlight = function highlight(event) {
  event = this.series.chart.pointer.normalize(event);
  this.onMouseOver(); // Show the hover marker
  this.series.chart.tooltip.refresh(this); // Show the tooltip
  this.series.chart.xAxis[0].drawCrosshair(event, this); // Show the crosshair
};


function RenderParamChartItem({ param, mousePosition }: { param: TParamsChartItem, mousePosition?: MouseEvent }) {

  const metric = useSelector<RootState>(state => state.user.user?.unit_system) as "metric" | "imperial" | undefined



  const { getUserDateFormat, getSpeedUnit } = useAuth()

  const [displayedValue, setDisplayedValue] = useState('')


  const chartRef = useRef<HTMLDivElement | null>(null)
  const chartInstance = useRef<Chart | null>(null);


  const onExamClicked = (exam?: Ttraining) => {
    if (!exam) return
    let route = routes.DASHBOARD_ROUTES.EXAM_VIEW.replace(':id', String(exam.id))
    window.open(route, '_blank')
  }

  useEffect(() => {
    if (!chartRef.current) return
    chartInstance.current = Highcharts.chart(chartRef.current, {
      chart: {
        type: 'scatter', // Defines the chart type as scatter
        backgroundColor: 'transparent',
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      legend: {
        enabled: false
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          point: {

            events: {
              mouseOver: function () {
                return false; // Disable mouseover event on points
              },
              mouseOut: function () {
                return false; // Disable mouseout event on points
              },
              click: function () {

                let exam = get(this, 'exam', undefined) as Ttraining | undefined

                onExamClicked(exam)
              }
            }
          },
          marker: {
            states: {
              hover: {
                enabled: false
              }
            }
          }

        }
      },
      xAxis: {
        type: 'datetime',
        title: {
          text: '',
        },
        crosshair: {
          width: 1,
        },
        gridLineColor: '#444444',
        lineColor: '#888888',
        tickColor: '#888888',
        labels: {
          format: '{value:%d/%m/%Y}', // Format for date labels
          style: {
            color: '#cccccc', // Light gray labels
            fontSize: '10px'
          },
        },
      },
      yAxis: {
        title: {
          text: '',
        },
        gridLineColor: '#444444',
        lineColor: '#888888',
        tickColor: '#888888',
        labels: {
          style: {
            color: '#cccccc', // Light gray labels
          },
        },
      },
      tooltip: {
        useHTML: true,
        backgroundColor: '#161C24',
        formatter: function () {
          let exam = get(this.point, 'options.exam', null) as Ttraining | null
          if (!exam) return ''
          return ` 
           <span style="color:gray;font-style: oblique;font-size:12px"> ${t('date')}  : </span> <span style='font-weight:500;font-size:12px;color:white'> ${dayjs(exam.creation_date).utc(true).format(getUserDateFormat)} </span> </br> 
           <span style="color:gray;font-style: oblique;font-size:12px"> ${t('eap:table:headers:trainingType')}  : </span> <span style='font-weight:500;font-size:12px;color:white'> ${exam.training_type?.display_name ?? '-'} </span> </br> 
           <span style="color:gray;font-style: oblique;font-size:12px"> ${t('eap:table:headers:trackName')}  : </span> <span style='font-weight:500;font-size:12px;color:white'> ${exam.track_name?.display_name ?? '-'} </span> </br> 
           <span style="color:gray;font-style: oblique;font-size:12px"> ${t('eap:table:headers:trackCondition')}  : </span> <span style='font-weight:500;font-size:12px;color:white'> ${exam.track_condition?.display_name ?? '-'} </span> </br> 
           <span style="color:gray;font-style: oblique;font-size:12px"> ${t('eap:table:headers:trackSurface')}  : </span> <span style='font-weight:500;font-size:12px;color:white'> ${exam.track_surface?.display_name ?? '-'} </span> </br> 
           `
        }
      },
      series: [],
    });

  }, [])


  const formatBasedOnUnit = (value: number, unit?: string) => {

    switch (unit) {
      case 'km/h':
        return toNumber(getSpeedForChart(toNumber(value), getSpeedUnit()?.value ?? ""))
      case 'm/stride':
        return toNumber(convertStrideLength(metric, toNumber(value)))
      case 'str/s':
        return toNumber(value) / 50

      default:
        return value
    }

  }

  const series = useMemo(() => {

    if (isEmpty(param.trainings)) return []


    let sortedExams = param.trainings.sort((t1, t2) => sortDate(t1.creation_date, t2.creation_date, 'ASC'))

    return sortedExams.map(exam => ({
      x: dayjs(exam?.creation_date).valueOf(),
      y: formatBasedOnUnit(exam.raw_numeric_value, param.param.key_parameter_layout.insight_parameter?.unit),
      exam,
      category: param.param.key_parameter_layout.insight_parameter.category
    }))


  }, [param.trainings])




  useEffect(() => {

    if (chartInstance.current) {
      chartInstance.current.addSeries({
        type: 'scatter',
        name: 'exam Series',
        data: series, // New data points
        color: param.param.key_parameter_layout.insight_parameter?.color
      });
    }


  }, [series])


  useEffect(() => {
    if (chartInstance.current && mousePosition?.clientX) {

      let chart = chartInstance.current

      if (!chart) return
      try {
        let event = chart?.pointer.normalize(mousePosition);

        if (!event) return

        chart?.pointer.reset();  // This will hide the tooltip and crosshair

        let series = chart?.series[0]

        // Create a new event object with only the x-coordinate
        const eventWithOnlyX = {
          ...event,
          chartX: event.chartX, // Ensure the x-axis position is intact
          chartY: undefined, // Remove the y-axis position
        };


        const point = event && series?.searchPoint(eventWithOnlyX, true);


        setDisplayedValue(get(point, 'exam.formatted_value', ''))

        /* @ts-ignore */
        point?.highlight?.(eventWithOnlyX);
      } catch (error) {
        console.error('error while trying to sync charts');

      }


    }
  }, [mousePosition])



  return (
    <Stack direction={'column'} width={'100%'} >
      <Box display={'flex'} alignItems={'center'} gap={0.5}>
        <Typography variant='h6'>
          {get(param, 'param.key_parameter_layout.insight_parameter.name_translated')}
        </Typography>
        <Tooltip placement='right' title={<span dangerouslySetInnerHTML={{ __html: get(param, 'param.key_parameter_layout.insight_parameter.details_translated', '') }}></span>} >
          <Icon icon={'material-symbols:info-outline-rounded'} />
        </Tooltip>
      </Box>
      <Typography sx={{ alignSelf: 'end' }} variant='subtitle2'>{displayedValue}</Typography>




      <div ref={chartRef} style={{ width: '100%', height: '220px', marginTop: 10, zIndex: 0, position: 'relative' }} />
    </Stack>
  )
}

export default RenderParamChartItem